import React, { useState, useRef, useMemo, useEffect } from "react";
import { db } from "../firebase";
import { collection, addDoc, onSnapshot, doc, updateDoc, deleteDoc, query, orderBy } from "firebase/firestore";
import useClients from "./useClients";


const CATEGORIES = ["Bookkeeping","Tax Preparation","CFO Advisory","Cleanup / Catchup","QBO Setup","Payroll Integration","Engagement Letter","Client Meeting","Admin","Other"];
// Team members loaded dynamically from Firestore
const STORAGE_KEY = "ficsori_time_entries_v3";

const S = {
  card:{background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:12,padding:"16px 20px",marginBottom:16},
  btn:{fontSize:12,padding:"5px 12px",borderRadius:8,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  btnPrimary:{fontSize:12,padding:"5px 12px",borderRadius:8,border:"none",background:"#C0111F",color:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  btnSm:{fontSize:11,padding:"3px 8px",borderRadius:6,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer"},
  btnGhost:{fontSize:11,padding:"2px 8px",borderRadius:6,border:"0.5px solid #e0dfd8",background:"#f5f5f0",cursor:"pointer",color:"#555"},
  input:{width:"100%",padding:"6px 9px",fontSize:12,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",background:"#fff",boxSizing:"border-box"},
  textarea:{width:"100%",padding:"6px 9px",fontSize:12,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",background:"#fff",boxSizing:"border-box",resize:"vertical"},
  th:{textAlign:"left",padding:"6px 10px",borderBottom:"0.5px solid #e0dfd8",color:"#888",fontWeight:500,fontSize:12,whiteSpace:"nowrap"},
  td:{padding:"7px 10px",borderBottom:"0.5px solid #f0efe8",fontSize:12,verticalAlign:"middle"},
  badge:(bg,c)=>({display:"inline-block",fontSize:10,padding:"2px 7px",borderRadius:20,background:bg,color:c,fontWeight:500}),
  sectionHead:{fontSize:11,fontWeight:500,color:"#1a1a18",marginBottom:6,marginTop:12,paddingBottom:4,borderBottom:"0.5px solid #e0dfd8"},
  noteSection:(color)=>({borderLeft:"2px solid "+color,paddingLeft:10,marginBottom:12}),
};

function fmt(s){ return String(Math.floor(s/3600)).padStart(2,"0")+":"+String(Math.floor((s%3600)/60)).padStart(2,"0")+":"+String(s%60).padStart(2,"0"); }
function fmtH(s){ const h=Math.floor(s/3600),m=Math.floor((s%3600)/60); if(h===0)return m+"m"; if(m===0)return h+"h"; return h+"h "+m+"m"; }
function fmtHDecimal(s){ return (s/3600).toFixed(2)+"h"; }

function loadEntries(){ try{ return JSON.parse(localStorage.getItem(STORAGE_KEY)||"[]"); }catch(e){ return []; } }
function saveEntries(e){ try{ localStorage.setItem(STORAGE_KEY,JSON.stringify(e)); }catch(e){} }

const BLANK_NOTES = { accomplished:"", reqDocs:[""], infoNeeded:[{date:"",amount:"",type:"Expense",memo:"",comment:""}], issues:["" ] };

function NotesPanel({ notes, setNotes, client, category }) {
  const updReqDoc = (i,v) => { const a=[...notes.reqDocs]; a[i]=v; setNotes(n=>({...n,reqDocs:a})); };
  const addReqDoc = () => setNotes(n=>({...n,reqDocs:[...n.reqDocs,""]}));
  const removeReqDoc = (i) => setNotes(n=>({...n,reqDocs:n.reqDocs.filter((_,x)=>x!==i)}));

  const updInfo = (i,k,v) => { const a=[...notes.infoNeeded]; a[i]={...a[i],[k]:v}; setNotes(n=>({...n,infoNeeded:a})); };
  const addInfo = () => setNotes(n=>({...n,infoNeeded:[...n.infoNeeded,{date:"",amount:"",type:"Expense",memo:"",comment:""}]}));
  const removeInfo = (i) => setNotes(n=>({...n,infoNeeded:n.infoNeeded.filter((_,x)=>x!==i)}));

  const updIssue = (i,v) => { const a=[...notes.issues]; a[i]=v; setNotes(n=>({...n,issues:a})); };
  const addIssue = () => setNotes(n=>({...n,issues:[...n.issues,""]}));
  const removeIssue = (i) => setNotes(n=>({...n,issues:n.issues.filter((_,x)=>x!==i)}));

  return (
    <div style={{...S.card,height:"100%",overflowY:"auto",marginBottom:0}}>
      <div style={{fontSize:13,fontWeight:500,marginBottom:4}}>Session Notes</div>
      {client && <div style={{fontSize:11,color:"#888",marginBottom:14}}>{client}{category?" · "+category:""}</div>}

      {/* Accomplished */}
      <div style={S.noteSection("#3B6D11")}>
        <div style={{...S.sectionHead,borderBottom:"none",marginTop:0,color:"#3B6D11"}}>Work Accomplished</div>
        <textarea style={{...S.textarea,minHeight:72}} value={notes.accomplished} onChange={e=>setNotes(n=>({...n,accomplished:e.target.value}))} placeholder="Describe what was completed this session — bank feeds reviewed, transactions categorized, accounts reconciled, etc."/>
      </div>

      {/* Required Documents */}
      <div style={S.noteSection("#185FA5")}>
        <div style={{...S.sectionHead,borderBottom:"none",marginTop:0,color:"#185FA5"}}>Required Documents</div>
        <div style={{fontSize:11,color:"#888",marginBottom:6}}>List statements or documents still needed from client or bank</div>
        {notes.reqDocs.map((doc,i)=>(
          <div key={i} style={{display:"flex",gap:6,marginBottom:6,alignItems:"center"}}>
            <div style={{fontSize:11,color:"#aaa",flexShrink:0,width:16,textAlign:"right"}}>{i+1}.</div>
            <input style={{...S.input,flex:1}} value={doc} onChange={e=>updReqDoc(i,e.target.value)} placeholder="e.g. Bank of America checking — March 2026 statement"/>
            <button style={{...S.btnSm,color:"#aaa",border:"none",background:"none",fontSize:16,lineHeight:1,flexShrink:0}} onClick={()=>removeReqDoc(i)}>×</button>
          </div>
        ))}
        <button style={S.btnGhost} onClick={addReqDoc}>+ Add document</button>
      </div>

      {/* Info Needed */}
      <div style={S.noteSection("#854F0B")}>
        <div style={{...S.sectionHead,borderBottom:"none",marginTop:0,color:"#854F0B"}}>Info Needed</div>
        <div style={{fontSize:11,color:"#888",marginBottom:8}}>Transaction questions — vague bank memos, unidentifiable checks, etc.</div>
        {notes.infoNeeded.map((item,i)=>(
          <div key={i} style={{background:"#fafaf8",border:"0.5px solid #e0dfd8",borderRadius:8,padding:"10px 12px",marginBottom:8,position:"relative"}}>
            <button style={{position:"absolute",top:6,right:8,color:"#aaa",border:"none",background:"none",fontSize:16,lineHeight:1,cursor:"pointer"}} onClick={()=>removeInfo(i)}>×</button>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
              <div>
                <div style={{fontSize:10,color:"#888",marginBottom:2}}>Date</div>
                <input style={S.input} type="date" value={item.date} onChange={e=>updInfo(i,"date",e.target.value)}/>
              </div>
              <div>
                <div style={{fontSize:10,color:"#888",marginBottom:2}}>Amount</div>
                <input style={S.input} value={item.amount} onChange={e=>updInfo(i,"amount",e.target.value)} placeholder="e.g. $75.00"/>
              </div>
              <div>
                <div style={{fontSize:10,color:"#888",marginBottom:2}}>Type</div>
                <select style={S.input} value={item.type} onChange={e=>updInfo(i,"type",e.target.value)}>
                  <option>Expense</option>
                  <option>Income</option>
                  <option>Transfer</option>
                  <option>Check</option>
                  <option>Deposit</option>
                  <option>Other</option>
                </select>
              </div>
            </div>
            <div style={{marginBottom:6}}>
              <div style={{fontSize:10,color:"#888",marginBottom:2}}>Bank memo</div>
              <input style={S.input} value={item.memo} onChange={e=>updInfo(i,"memo",e.target.value)} placeholder="Paste the bank memo exactly as it appears"/>
            </div>
            <div>
              <div style={{fontSize:10,color:"#888",marginBottom:2}}>Comment</div>
              <textarea style={{...S.textarea,minHeight:48}} value={item.comment} onChange={e=>updInfo(i,"comment",e.target.value)} placeholder="e.g. Tried Googling this memo — could be a vendor payment but unsure. Client may know."/>
            </div>
          </div>
        ))}
        <button style={S.btnGhost} onClick={addInfo}>+ Add transaction question</button>
      </div>

      {/* Issues */}
      <div style={S.noteSection("#A32D2D")}>
        <div style={{...S.sectionHead,borderBottom:"none",marginTop:0,color:"#A32D2D"}}>Issues</div>
        <div style={{fontSize:11,color:"#888",marginBottom:6}}>Disconnected bank feeds, reconciliation discrepancies, payroll mismatches, etc.</div>
        {notes.issues.map((issue,i)=>(
          <div key={i} style={{display:"flex",gap:6,marginBottom:6,alignItems:"flex-start"}}>
            <div style={{fontSize:11,color:"#aaa",flexShrink:0,width:16,textAlign:"right",marginTop:7}}>!</div>
            <textarea style={{...S.textarea,flex:1,minHeight:48}} value={issue} onChange={e=>updIssue(i,e.target.value)} placeholder="Describe the issue — what's wrong, what was tried, what's still unresolved"/>
            <button style={{...S.btnSm,color:"#aaa",border:"none",background:"none",fontSize:16,lineHeight:1,flexShrink:0,marginTop:4}} onClick={()=>removeIssue(i)}>×</button>
          </div>
        ))}
        <button style={S.btnGhost} onClick={addIssue}>+ Add issue</button>
      </div>
    </div>
  );
}

function NotesBadges({ notes }) {
  if(!notes) return null;
  const hasDocs = notes.reqDocs?.some(d=>d.trim());
  const hasInfo = notes.infoNeeded?.some(i=>i.memo||i.comment);
  const hasIssues = notes.issues?.some(i=>i.trim());
  const hasWork = notes.accomplished?.trim();
  return (
    <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:4}}>
      {hasWork && <span style={S.badge("#EAF3DE","#3B6D11")}>Work noted</span>}
      {hasDocs && <span style={S.badge("#E6F1FB","#185FA5")}>Docs needed</span>}
      {hasInfo && <span style={S.badge("#FAEEDA","#854F0B")}>Info needed</span>}
      {hasIssues && <span style={S.badge("#FCEBEB","#A32D2D")}>Issues</span>}
    </div>
  );
}

function NotesViewModal({ entry, onClose, onSave }) {
  const [notes, setNotes] = useState(entry.notes || { ...BLANK_NOTES, reqDocs:[""], infoNeeded:[{date:"",amount:"",type:"Expense",memo:"",comment:""}], issues:[""] });
  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
      <div style={{background:"#f5f5f0",borderRadius:16,width:680,maxHeight:"88vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 20px",background:"#fff",borderBottom:"0.5px solid #e0dfd8"}}>
          <div>
            <div style={{fontSize:14,fontWeight:500}}>{entry.client}</div>
            <div style={{fontSize:11,color:"#888"}}>{entry.user} · {entry.category} · {entry.date} · {entry.duration}</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button style={{...S.btnPrimary}} onClick={()=>onSave(notes)}>Save Notes</button>
            <button style={S.btn} onClick={onClose}>Close</button>
          </div>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:16}}>
          <NotesPanel notes={notes} setNotes={setNotes} client={entry.client} category={entry.category}/>
        </div>
      </div>
    </div>
  );
}

const DATE_RANGE_OPTIONS = ["All","Today","This Week to Date","Last Week","Last Week to Date","This Month to Date","Last Month","Last Month to Date","This Year to Date","Last Year","Last Year to Date","Custom"];

function getDateRange(range, customFrom, customTo){
  const now=new Date(), today=new Date(now.getFullYear(),now.getMonth(),now.getDate()), todayEnd=new Date(today.getTime()+86399999);
  const sow=(d)=>{const s=new Date(d);s.setDate(d.getDate()-d.getDay());s.setHours(0,0,0,0);return s;};
  const eow=(d)=>{const e=new Date(d);e.setDate(d.getDate()+(6-d.getDay()));e.setHours(23,59,59,999);return e;};
  const som=(d)=>new Date(d.getFullYear(),d.getMonth(),1);
  const eom=(d)=>new Date(d.getFullYear(),d.getMonth()+1,0,23,59,59,999);
  const soy=(d)=>new Date(d.getFullYear(),0,1);
  const eoy=(d)=>new Date(d.getFullYear(),11,31,23,59,59,999);
  const lws=sow(new Date(today.getTime()-7*86400000)), lwd=new Date(now.getFullYear(),now.getMonth()-1,1), ly=new Date(now.getFullYear()-1,0,1);
  const map={
    "All":[new Date(0),new Date(9999,0,1)],"Today":[today,todayEnd],
    "This Week to Date":[sow(today),todayEnd],"Last Week":[lws,eow(new Date(today.getTime()-7*86400000))],
    "Last Week to Date":[lws,todayEnd],"This Month to Date":[som(today),todayEnd],
    "Last Month":[som(lwd),eom(lwd)],"Last Month to Date":[som(lwd),todayEnd],
    "This Year to Date":[soy(today),todayEnd],"Last Year":[soy(ly),eoy(ly)],
    "Last Year to Date":[soy(ly),todayEnd],
    "Custom":[customFrom?new Date(customFrom):new Date(0),customTo?new Date(customTo+"T23:59:59"):new Date(9999,0,1)],
  };
  return map[range]||map["All"];
}

export default function TimeTracking({ assignedClients, currentUser }){
  const { clientNames: CLIENT_NAMES } = useClients(assignedClients);
  const [teamMembers, setTeamMembers] = useState(["Frank Ficsori"]);

  useEffect(()=>{
    const unsub = onSnapshot(collection(db,"team"), snap=>{
      const names = snap.docs.map(d=>d.data().name).filter(Boolean).sort();
      setTeamMembers(["Frank Ficsori", ...names]);
    });
    return ()=>unsub();
  },[]);
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [running, setRunning] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [timerClient, setTimerClient] = useState("");
  const [timerCategory, setTimerCategory] = useState("");
  const [timerUser, setTimerUser] = useState(teamMembers[0]);
  const [notes, setNotes] = useState({...BLANK_NOTES,reqDocs:[""],infoNeeded:[{date:"",amount:"",type:"Expense",memo:"",comment:""}],issues:[""]});
  const [view, setView] = useState("timer");
  const [filterClient, setFilterClient] = useState("All Clients");
  const [filterUser, setFilterUser] = useState("All Users");
  const [filterRange, setFilterRange] = useState("All");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [reportType, setReportType] = useState("by-client");
  const [notesModal, setNotesModal] = useState(null);
  const intervalRef = useRef(null);

  // Firestore real-time listener
  useEffect(()=>{
    const q = query(collection(db,"timeEntries"), orderBy("timestamp","desc"));
    const unsub = onSnapshot(q, snap=>{
      setEntries(snap.docs.map(d=>({...d.data(), firestoreId:d.id})));
      setLoadingEntries(false);
    }, err=>{ console.error(err); setLoadingEntries(false); });
    return ()=>unsub();
  },[]);

  const toggle = () => {
    if(running){ clearInterval(intervalRef.current); setRunning(false); }
    else{ intervalRef.current=setInterval(()=>setSeconds(s=>s+1),1000); setRunning(true); }
  };

  const stop = async () => {
    clearInterval(intervalRef.current); setRunning(false);
    if(seconds>0){
      const entry={
        client:timerClient||"No client", category:timerCategory||"Other", user:timerUser,
        seconds, duration:fmtH(seconds),
        date:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}),
        timestamp:Date.now(), notes:{...notes},
      };
      try{ await addDoc(collection(db,"timeEntries"), entry); }
      catch(e){ console.error("Failed to save time entry:",e); }
      setNotes({...BLANK_NOTES,reqDocs:[""],infoNeeded:[{date:"",amount:"",type:"Expense",memo:"",comment:""}],issues:[""]});
    }
    setSeconds(0);
  };

  const deleteEntry = async (firestoreId) => {
    try{ await deleteDoc(doc(db,"timeEntries",firestoreId)); }
    catch(e){ console.error("Delete failed:",e); }
  };
  const saveEntryNotes = async (firestoreId, newNotes) => {
    try{
      await updateDoc(doc(db,"timeEntries",firestoreId),{notes:newNotes});
      setNotesModal(null);
    }catch(e){ console.error("Notes save failed:",e); }
  };

  const applyFilters = (list) => {
    const [from,to]=getDateRange(filterRange,customFrom,customTo);
    return list.filter(e=>{
      if(filterClient!=="All Clients"&&e.client!==filterClient) return false;
      if(filterUser!=="All Users"&&e.user!==filterUser) return false;
      const ts=new Date(e.timestamp);
      return ts>=from&&ts<=to;
    });
  };

  const filtered = useMemo(()=>applyFilters(entries),[entries,filterClient,filterUser,filterRange,customFrom,customTo]);
  const totalSeconds = filtered.reduce((s,e)=>s+e.seconds,0);

  const clientSummary = useMemo(()=>{
    const acc={};
    filtered.forEach(e=>{
      if(!acc[e.client]) acc[e.client]={client:e.client,seconds:0,entries:0};
      acc[e.client].seconds+=e.seconds; acc[e.client].entries+=1;
    });
    return Object.values(acc).sort((a,b)=>b.seconds-a.seconds);
  },[filtered]);

  const userSummary = useMemo(()=>{
    const acc={};
    filtered.forEach(e=>{
      const u=e.user||teamMembers[0];
      if(!acc[u]) acc[u]={user:u,seconds:0,entries:0,clients:new Set()};
      acc[u].seconds+=e.seconds; acc[u].entries+=1; acc[u].clients.add(e.client);
    });
    return Object.values(acc).sort((a,b)=>b.seconds-a.seconds);
  },[filtered]);

  const FilterBar = () => (
    <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
      <select style={{...S.input,width:220}} value={filterClient} onChange={e=>setFilterClient(e.target.value)}>
        <option>All Clients</option>{CLIENT_NAMES.map(c=><option key={c}>{c}</option>)}
      </select>
      <select style={{...S.input,width:160}} value={filterUser} onChange={e=>setFilterUser(e.target.value)}>
        <option>All Users</option>{teamMembers.map(u=><option key={u}>{u}</option>)}
      </select>
      <select style={{...S.input,width:190}} value={filterRange} onChange={e=>setFilterRange(e.target.value)}>
        {DATE_RANGE_OPTIONS.map(r=><option key={r}>{r}</option>)}
      </select>
      {filterRange==="Custom"&&<>
        <input type="date" style={{...S.input,width:140}} value={customFrom} onChange={e=>setCustomFrom(e.target.value)}/>
        <span style={{fontSize:12,color:"#888"}}>to</span>
        <input type="date" style={{...S.input,width:140}} value={customTo} onChange={e=>setCustomTo(e.target.value)}/>
      </>}
      <div style={{fontSize:12,color:"#888",whiteSpace:"nowrap"}}>{filtered.length} entries · {fmtH(totalSeconds)}</div>
    </div>
  );

  const printReport = () => {
    const rows = reportType==="by-user"
      ? userSummary.map(r=>`<tr><td>${r.user}</td><td>${r.clients.size}</td><td>${r.entries}</td><td>${fmtH(r.seconds)}</td><td>${fmtHDecimal(r.seconds)}</td></tr>`).join("")
      : reportType==="by-client"
      ? clientSummary.map(r=>`<tr><td>${r.client}</td><td>${r.entries}</td><td>${fmtH(r.seconds)}</td><td>${fmtHDecimal(r.seconds)}</td></tr>`).join("")
      : filtered.map(e=>`<tr><td>${e.user||teamMembers[0]}</td><td>${e.client}</td><td>${e.category}</td><td>${e.date}</td><td>${e.duration}</td></tr>`).join("");
    const headers = reportType==="by-user"?"<tr><th>User</th><th>Clients</th><th>Entries</th><th>Time</th><th>Decimal</th></tr>"
      :reportType==="by-client"?"<tr><th>Client</th><th>Entries</th><th>Time</th><th>Decimal</th></tr>"
      :"<tr><th>User</th><th>Client</th><th>Category</th><th>Date</th><th>Time</th></tr>";
    const title = reportType==="by-user"?"Time Report — By Team Member":reportType==="by-client"?"Time Report — By Client":"Time Log — Full Detail";
    const w=window.open("","_blank");
    w.document.write(`<html><head><title>${title}</title><style>body{font-family:sans-serif;padding:24px;color:#1a1a18}h2{color:#C0111F;margin-bottom:4px}table{width:100%;border-collapse:collapse;margin-top:16px}th{text-align:left;padding:8px 12px;background:#f5f5f0;border-bottom:1px solid #ddd;font-size:13px}td{padding:7px 12px;border-bottom:1px solid #eee;font-size:13px}.meta{font-size:12px;color:#888;margin-bottom:3px}</style></head><body><h2>Ficsori Accounting</h2><div class="meta">${title}</div><div class="meta">Range: ${filterRange}${filterRange==="Custom"?" ("+customFrom+" → "+customTo+")":""} · Client: ${filterClient} · User: ${filterUser}</div><div class="meta">Total: ${fmtH(totalSeconds)} (${fmtHDecimal(totalSeconds)})</div><table><thead>${headers}</thead><tbody>${rows}</tbody></table></body></html>`);
    w.document.close(); w.print();
  };

  return (
    <div>
      {notesModal && <NotesViewModal entry={notesModal} onClose={()=>setNotesModal(null)} onSave={(n)=>saveEntryNotes(notesModal.firestoreId,n)}/>}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={{fontSize:14,fontWeight:500}}>Time Tracking</div>
        <div style={{display:"flex",gap:6}}>
          {[["timer","Timer"],["log","Time Log"],["summary","Summary"],["reports","Reports"]].map(([v,l])=>(
            <button key={v} onClick={()=>setView(v)} style={{...S.btn,background:view===v?"#C0111F":"#fff",color:view===v?"#fff":"#555",border:"0.5px solid "+(view===v?"#C0111F":"#ccc"),fontWeight:view===v?500:400}}>{l}</button>
          ))}
        </div>
      </div>

      {view==="timer" && (
        <div style={{display:"grid",gridTemplateColumns:"320px 1fr",gap:16,alignItems:"start"}}>
          {/* Left: Timer controls */}
          <div style={S.card}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:14}}>Active Timer</div>
            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,color:"#888",marginBottom:3}}>Team member</div>
              <select style={S.input} value={timerUser} onChange={e=>setTimerUser(e.target.value)}>
                {teamMembers.map(u=><option key={u}>{u}</option>)}
              </select>
            </div>
            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,color:"#888",marginBottom:3}}>Client</div>
              <select style={S.input} value={timerClient} onChange={e=>setTimerClient(e.target.value)}>
                <option value="">Select client...</option>
                {CLIENT_NAMES.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,color:"#888",marginBottom:3}}>Category</div>
              <select style={S.input} value={timerCategory} onChange={e=>setTimerCategory(e.target.value)}>
                <option value="">Select category...</option>
                {CATEGORIES.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div style={{fontSize:34,fontWeight:500,color:running?"#C0111F":"#aaa",textAlign:"center",letterSpacing:"0.06em",margin:"14px 0 12px",fontFamily:"monospace"}}>{fmt(seconds)}</div>
            <div style={{display:"flex",gap:8,marginBottom:10}}>
              <button style={{...S.btn,flex:1,fontWeight:500,borderColor:running?"#C0111F":"#ccc",color:running?"#C0111F":"#555"}} onClick={toggle}>{running?"⏸ Pause":"▶ Start"}</button>
              <button style={{...S.btnPrimary,flex:1}} onClick={stop} disabled={seconds===0}>■ Stop & Log</button>
            </div>
            {running&&timerClient&&<div style={{padding:"7px 10px",background:"#fdf0f0",borderRadius:8,fontSize:12,color:"#C0111F",textAlign:"center",marginBottom:10}}>Tracking: {timerUser} · {timerClient}</div>}
            <div style={{borderTop:"0.5px solid #e0dfd8",paddingTop:10,marginTop:4}}>
              <div style={{fontSize:11,color:"#888",marginBottom:6}}>Recent Entries</div>
              {entries.length===0
                ? <div style={{fontSize:12,color:"#aaa",textAlign:"center",padding:"12px 0"}}>No entries yet.</div>
                : entries.slice(0,5).map(e=>(
                  <div key={e.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:"0.5px solid #f0efe8"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.client}</div>
                      <div style={{fontSize:10,color:"#888"}}>{e.user} · {e.date}</div>
                      <NotesBadges notes={e.notes}/>
                    </div>
                    <div style={{fontSize:12,fontWeight:500,color:"#C0111F",flexShrink:0}}>{e.duration}</div>
                    <button style={{...S.btnGhost,fontSize:10,padding:"2px 6px",flexShrink:0}} onClick={()=>setNotesModal(e)}>Notes</button>
                  </div>
                ))
              }
            </div>
          </div>

          {/* Right: Notes panel */}
          <NotesPanel notes={notes} setNotes={setNotes} client={timerClient} category={timerCategory}/>
        </div>
      )}

      {view==="log" && (
        <div style={S.card}>
          <FilterBar/>
          {filtered.length===0
            ? <div style={{fontSize:13,color:"#aaa",textAlign:"center",padding:"32px 0"}}>No entries match your filters.</div>
            : <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr>
                  <th style={S.th}>User</th><th style={S.th}>Client</th><th style={S.th}>Category</th>
                  <th style={S.th}>Date</th><th style={S.th}>Time</th><th style={S.th}>Notes</th><th style={S.th}></th>
                </tr></thead>
                <tbody>
                  {filtered.map(e=>(
                    <tr key={e.id}>
                      <td style={S.td}><span style={S.badge(e.user===teamMembers[0]?"#E6F1FB":"#EAF3DE",e.user===teamMembers[0]?"#185FA5":"#3B6D11")}>{e.user||teamMembers[0]}</span></td>
                      <td style={{...S.td,fontWeight:500}}>{e.client}</td>
                      <td style={S.td}><span style={S.badge("#f5f5f0","#555")}>{e.category}</span></td>
                      <td style={{...S.td,color:"#888",whiteSpace:"nowrap"}}>{e.date}</td>
                      <td style={{...S.td,fontWeight:500,color:"#C0111F",whiteSpace:"nowrap"}}>{e.duration}</td>
                      <td style={S.td}><NotesBadges notes={e.notes}/></td>
                      <td style={S.td}>
                        <div style={{display:"flex",gap:4}}>
                          <button style={S.btnGhost} onClick={()=>setNotesModal(e)}>View Notes</button>
                          <button style={{...S.btnSm,color:"#aaa",border:"none",background:"none",fontSize:16,lineHeight:1}} onClick={()=>deleteEntry(e.firestoreId)}>×</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          }
        </div>
      )}

      {view==="summary" && (
        <div>
          <FilterBar/>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
            {[["Total Hours",fmtH(totalSeconds),"#C0111F"],["Clients",clientSummary.length,"#185FA5"],["Entries",filtered.length,"#3B6D11"]].map(([l,v,c])=>(
              <div key={l} style={{background:"#f5f5f0",borderRadius:8,padding:"14px 16px"}}><div style={{fontSize:12,color:"#888",marginBottom:6}}>{l}</div><div style={{fontSize:22,fontWeight:500,color:c}}>{v}</div></div>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            <div style={S.card}>
              <div style={{fontSize:13,fontWeight:500,marginBottom:12}}>Hours by Client</div>
              {clientSummary.length===0?<div style={{fontSize:13,color:"#aaa",textAlign:"center",padding:"20px 0"}}>No data.</div>
                :clientSummary.map(r=>(
                  <div key={r.client} style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}><span style={{fontWeight:500}}>{r.client}</span><span style={{color:"#C0111F",fontWeight:500}}>{fmtH(r.seconds)}</span></div>
                    <div style={{height:4,background:"#f0efe8",borderRadius:2}}><div style={{height:4,background:"#C0111F",borderRadius:2,width:Math.round(r.seconds/totalSeconds*100)+"%"}}/></div>
                    <div style={{fontSize:11,color:"#aaa",marginTop:2}}>{r.entries} {r.entries===1?"entry":"entries"} · {Math.round(r.seconds/totalSeconds*100)}%</div>
                  </div>
                ))
              }
            </div>
            <div style={S.card}>
              <div style={{fontSize:13,fontWeight:500,marginBottom:12}}>Hours by Team Member</div>
              {userSummary.length===0?<div style={{fontSize:13,color:"#aaa",textAlign:"center",padding:"20px 0"}}>No data.</div>
                :userSummary.map(r=>(
                  <div key={r.user} style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}><span style={{fontWeight:500}}>{r.user}</span><span style={{color:"#185FA5",fontWeight:500}}>{fmtH(r.seconds)}</span></div>
                    <div style={{height:4,background:"#f0efe8",borderRadius:2}}><div style={{height:4,background:"#185FA5",borderRadius:2,width:Math.round(r.seconds/totalSeconds*100)+"%"}}/></div>
                    <div style={{fontSize:11,color:"#aaa",marginTop:2}}>{r.clients.size} {r.clients.size===1?"client":"clients"} · {r.entries} entries</div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {view==="reports" && (
        <div style={S.card}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontSize:13,fontWeight:500}}>Time Reports</div>
            <button style={S.btnPrimary} onClick={printReport}>Print / Export</button>
          </div>
          <div style={{display:"flex",gap:8,marginBottom:14}}>
            {[["by-client","By Client"],["by-user","By Team Member"],["detail","Full Detail"]].map(([v,l])=>(
              <button key={v} onClick={()=>setReportType(v)} style={{...S.btn,background:reportType===v?"#1a1a18":"#fff",color:reportType===v?"#fff":"#555",border:"0.5px solid "+(reportType===v?"#1a1a18":"#ccc")}}>{l}</button>
            ))}
          </div>
          <FilterBar/>
          {reportType==="by-client"&&(
            <>
              <div style={{fontSize:12,color:"#888",marginBottom:10}}>{fmtH(totalSeconds)} across {clientSummary.length} clients</div>
              {clientSummary.length===0?<div style={{fontSize:13,color:"#aaa",textAlign:"center",padding:"24px 0"}}>No data.</div>
                :<table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr><th style={S.th}>Client</th><th style={S.th}>Entries</th><th style={S.th}>Total Time</th><th style={S.th}>Decimal</th><th style={S.th}>% Share</th></tr></thead>
                  <tbody>
                    {clientSummary.map(r=>(
                      <tr key={r.client}>
                        <td style={{...S.td,fontWeight:500}}>{r.client}</td>
                        <td style={{...S.td,color:"#888"}}>{r.entries}</td>
                        <td style={{...S.td,fontWeight:500,color:"#C0111F"}}>{fmtH(r.seconds)}</td>
                        <td style={{...S.td,color:"#888"}}>{fmtHDecimal(r.seconds)}</td>
                        <td style={S.td}><div style={{display:"flex",alignItems:"center",gap:8}}><div style={{width:80,height:4,background:"#f0efe8",borderRadius:2}}><div style={{height:4,background:"#C0111F",borderRadius:2,width:Math.round(r.seconds/totalSeconds*100)+"%"}}/></div><span style={{fontSize:11,color:"#888"}}>{Math.round(r.seconds/totalSeconds*100)}%</span></div></td>
                      </tr>
                    ))}
                    <tr style={{background:"#f5f5f0"}}><td style={{...S.td,fontWeight:500}}>TOTAL</td><td style={{...S.td,fontWeight:500}}>{filtered.length}</td><td style={{...S.td,fontWeight:500,color:"#C0111F"}}>{fmtH(totalSeconds)}</td><td style={{...S.td,fontWeight:500}}>{fmtHDecimal(totalSeconds)}</td><td style={{...S.td,fontWeight:500}}>100%</td></tr>
                  </tbody>
                </table>
              }
            </>
          )}
          {reportType==="by-user"&&(
            <>
              <div style={{fontSize:12,color:"#888",marginBottom:10}}>{fmtH(totalSeconds)} across {userSummary.length} team member{userSummary.length!==1?"s":""}</div>
              {userSummary.length===0?<div style={{fontSize:13,color:"#aaa",textAlign:"center",padding:"24px 0"}}>No data.</div>
                :<table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr><th style={S.th}>Team Member</th><th style={S.th}>Clients</th><th style={S.th}>Entries</th><th style={S.th}>Total Time</th><th style={S.th}>Decimal</th><th style={S.th}>% Share</th></tr></thead>
                  <tbody>
                    {userSummary.map(r=>(
                      <tr key={r.user}>
                        <td style={{...S.td,fontWeight:500}}>{r.user}</td>
                        <td style={{...S.td,color:"#888"}}>{r.clients.size}</td>
                        <td style={{...S.td,color:"#888"}}>{r.entries}</td>
                        <td style={{...S.td,fontWeight:500,color:"#185FA5"}}>{fmtH(r.seconds)}</td>
                        <td style={{...S.td,color:"#888"}}>{fmtHDecimal(r.seconds)}</td>
                        <td style={S.td}><div style={{display:"flex",alignItems:"center",gap:8}}><div style={{width:80,height:4,background:"#f0efe8",borderRadius:2}}><div style={{height:4,background:"#185FA5",borderRadius:2,width:Math.round(r.seconds/totalSeconds*100)+"%"}}/></div><span style={{fontSize:11,color:"#888"}}>{Math.round(r.seconds/totalSeconds*100)}%</span></div></td>
                      </tr>
                    ))}
                    <tr style={{background:"#f5f5f0"}}><td style={{...S.td,fontWeight:500}}>TOTAL</td><td style={{...S.td,fontWeight:500}}>{new Set(filtered.map(e=>e.client)).size}</td><td style={{...S.td,fontWeight:500}}>{filtered.length}</td><td style={{...S.td,fontWeight:500,color:"#185FA5"}}>{fmtH(totalSeconds)}</td><td style={{...S.td,fontWeight:500}}>{fmtHDecimal(totalSeconds)}</td><td style={{...S.td,fontWeight:500}}>100%</td></tr>
                  </tbody>
                </table>
              }
            </>
          )}
          {reportType==="detail"&&(
            <>
              <div style={{fontSize:12,color:"#888",marginBottom:10}}>Entry-by-entry detail</div>
              {filtered.length===0?<div style={{fontSize:13,color:"#aaa",textAlign:"center",padding:"24px 0"}}>No data.</div>
                :<table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr><th style={S.th}>User</th><th style={S.th}>Client</th><th style={S.th}>Category</th><th style={S.th}>Date</th><th style={S.th}>Time</th><th style={S.th}>Notes</th></tr></thead>
                  <tbody>
                    {filtered.map(e=>(
                      <tr key={e.id}>
                        <td style={S.td}>{e.user||teamMembers[0]}</td>
                        <td style={{...S.td,fontWeight:500}}>{e.client}</td>
                        <td style={{...S.td,color:"#888"}}>{e.category}</td>
                        <td style={{...S.td,color:"#888",whiteSpace:"nowrap"}}>{e.date}</td>
                        <td style={{...S.td,fontWeight:500,color:"#C0111F"}}>{e.duration}</td>
                        <td style={S.td}><NotesBadges notes={e.notes}/></td>
                      </tr>
                    ))}
                    <tr style={{background:"#f5f5f0"}}><td colSpan={4} style={{...S.td,fontWeight:500,textAlign:"right"}}>TOTAL</td><td style={{...S.td,fontWeight:500,color:"#C0111F"}}>{fmtH(totalSeconds)}</td><td style={S.td}></td></tr>
                  </tbody>
                </table>
              }
            </>
          )}
        </div>
      )}
    </div>
  );
}
